import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { getSession } from "../utils/session";
import { API_URL, ORIGEN_API } from "../services/api";
import SesionEntreno from "./SesionEntreno";
import { FiAward, FiCheckCircle, FiClock, FiPlay, FiTrendingUp } from "react-icons/fi";
import "./RutinaPanel.css";

/*
 * Pantalla "Rutina": la sesión que toca hoy, el resto de la semana y el
 * historial de lo entrenado. Desde aquí se entra al modo entrenamiento.
 *
 * Sustituye a la lista estática anterior (PersonalizedRoutine), que sigue en
 * el proyecto por si hace falta volver atrás.
 */

const fecha = (d) =>
  new Date(d).toLocaleDateString("es-ES", { day: "numeric", month: "short" });

const duracion = (seg) => {
  if (!seg) return "—";
  const m = Math.round(seg / 60);
  return m >= 60 ? `${Math.floor(m / 60)} h ${m % 60} min` : `${m} min`;
};

/* ─── Una sesión de la rutina ─── */
function TarjetaSesion({ sesion, indice, esHoy, ultimaVez, onEmpezar, cargando }) {
  const total = sesion.ejercicios.length;
  const series = sesion.ejercicios.reduce((t, e) => t + (e.series || 3), 0);

  return (
    <article className={`rp-sesion${esHoy ? " rp-sesion-hoy" : ""}`}>
      <div className="rp-sesion-txt">
        <div className="rp-sesion-eti">
          {esHoy && <span className="rp-chip">Te toca hoy</span>}
          {ultimaVez && <span className="rp-sesion-ultima">Última vez: {fecha(ultimaVez)}</span>}
        </div>
        <h3>{sesion.nombre}</h3>
        <p className="rp-sesion-meta">
          {total} {total === 1 ? "ejercicio" : "ejercicios"} · {series} series
        </p>
        <ul className="rp-sesion-lista">
          {sesion.ejercicios.slice(0, 4).map((e, k) => (
            <li key={k}>{e.nombre}</li>
          ))}
          {total > 4 && <li className="rp-sesion-mas">y {total - 4} más</li>}
        </ul>
      </div>
      <button
        type="button"
        className="rp-empezar"
        onClick={() => onEmpezar(indice)}
        disabled={cargando}
      >
        <FiPlay /> {cargando ? "Abriendo…" : "Empezar"}
      </button>
    </article>
  );
}

/* ─── Resumen al terminar ─── */
function Enhorabuena({ log, onCerrar }) {
  return (
    <div className="rp-fin">
      <div className="rp-fin-caja">
        <FiCheckCircle className="rp-fin-icono" />
        <h2>Entrenamiento completado</h2>
        <p className="rp-fin-sesion">{log.sesionNombre}</p>
        <div className="rp-fin-cifras">
          <div>
            <strong>{duracion(log.duracionSeg)}</strong>
            <span>duración</span>
          </div>
          <div>
            <strong>{log.seriesCompletadas}</strong>
            <span>series</span>
          </div>
          <div>
            <strong>{(log.volumenKg || 0).toLocaleString("es-ES")}</strong>
            <span>kg movidos</span>
          </div>
        </div>
        <button type="button" className="rp-btn" onClick={onCerrar}>
          Hecho
        </button>
      </div>
    </div>
  );
}

function RutinaPanel({ userId, onResumenCambiado }) {
  const [rutina, setRutina] = useState(undefined); // undefined = cargando
  const [activa, setActiva] = useState(null);
  const [historial, setHistorial] = useState([]);
  const [resumen, setResumen] = useState(null);
  const [terminada, setTerminada] = useState(null);
  const [abriendo, setAbriendo] = useState(false);
  const [error, setError] = useState("");

  const cargar = useCallback(async () => {
    if (!userId) return;
    try {
      const { token } = await getSession();
      const cfg = { headers: { Authorization: `Bearer ${token}` } };

      const [rRutina, rActiva, rHist, rResumen] = await Promise.allSettled([
        axios.get(`${API_URL}/personal-routines/${userId}`, cfg),
        axios.get(`${API_URL}/workouts/activa`, cfg),
        axios.get(`${API_URL}/workouts`, { ...cfg, params: { limite: 12 } }),
        axios.get(`${API_URL}/workouts/resumen`, cfg),
      ]);

      setRutina(rRutina.status === "fulfilled" ? rRutina.value.data : null);
      setActiva(rActiva.status === "fulfilled" ? rActiva.value.data : null);
      setHistorial(rHist.status === "fulfilled" ? rHist.value.data : []);
      if (rResumen.status === "fulfilled") {
        setResumen(rResumen.value.data);
        onResumenCambiado?.(rResumen.value.data);
      }
      setError("");
    } catch {
      setError("No se pudo cargar tu rutina.");
      setRutina(null);
    }
  }, [userId, onResumenCambiado]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  const empezar = async (indice) => {
    setAbriendo(true);
    setError("");
    try {
      const { token } = await getSession();
      const res = await axios.post(
        `${API_URL}/workouts/empezar`,
        { sesionIndice: indice },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setActiva(res.data);
    } catch (err) {
      setError(err.response?.data?.error || "No se pudo empezar el entrenamiento.");
    } finally {
      setAbriendo(false);
    }
  };

  /* Entrenando: la pantalla de sesión ocupa todo */
  if (activa) {
    return (
      <SesionEntreno
        log={activa}
        onSalir={() => {
          setActiva(null);
          cargar();
        }}
        onTerminar={(log) => {
          setActiva(null);
          setTerminada(log);
          cargar();
        }}
      />
    );
  }

  if (rutina === undefined) return <div className="app-estado">Cargando tu rutina…</div>;

  /* Sin rutina asignada */
  if (!rutina?.sesiones?.length) {
    return (
      <section className="rp-vacio">
        <h2>Aún no tienes rutina</h2>
        <p>
          Tu entrenador todavía no te ha asignado una. Pídesela por Mensajes y aparecerá aquí.
        </p>
        {error && <p className="app-estado-error">{error}</p>}
      </section>
    );
  }

  // Cuál toca: la siguiente a la última que hizo, dando la vuelta al acabar
  const ultimoIndice = historial[0]?.sesionIndice;
  const siguiente =
    ultimoIndice === undefined ? 0 : (ultimoIndice + 1) % rutina.sesiones.length;

  // Última vez que hizo cada sesión
  const ultimaPorSesion = {};
  for (const log of historial) {
    if (log.sesionIndice !== undefined && !ultimaPorSesion[log.sesionIndice]) {
      ultimaPorSesion[log.sesionIndice] = log.empezadoEn;
    }
  }

  return (
    <div className="rp">
      {terminada && <Enhorabuena log={terminada} onCerrar={() => setTerminada(null)} />}

      {/* Cifras del mes */}
      {resumen && (
        <section className="rp-cifras">
          <div>
            <FiAward />
            <strong>{resumen.racha}</strong>
            <span>{resumen.racha === 1 ? "día seguido" : "días seguidos"}</span>
          </div>
          <div>
            <FiCheckCircle />
            <strong>{resumen.entrenosMes}</strong>
            <span>este mes</span>
          </div>
          <div>
            <FiTrendingUp />
            <strong>{(resumen.volumenMes || 0).toLocaleString("es-ES")}</strong>
            <span>kg movidos</span>
          </div>
          <div>
            <FiClock />
            <strong>{resumen.minutosMes}</strong>
            <span>minutos</span>
          </div>
        </section>
      )}

      {rutina.indicaciones && (
        <p className="rp-indicaciones">{rutina.indicaciones}</p>
      )}

      {/* Sesiones */}
      <section className="rp-bloque">
        <h2 className="rp-titulo">{rutina.nombre || "Tu rutina"}</h2>
        <div className="rp-sesiones">
          {rutina.sesiones.map((s, i) => (
            <TarjetaSesion
              key={i}
              sesion={s}
              indice={i}
              esHoy={i === siguiente}
              ultimaVez={ultimaPorSesion[i]}
              onEmpezar={empezar}
              cargando={abriendo}
            />
          ))}
        </div>
        {error && <p className="app-estado-error">{error}</p>}
      </section>

      {/* Historial */}
      {historial.length > 0 && (
        <section className="rp-bloque">
          <h2 className="rp-titulo">Lo que has entrenado</h2>
          <ul className="rp-historial">
            {historial.map((log) => (
              <li key={log._id}>
                <div className="rp-hist-txt">
                  <strong>{log.sesionNombre || "Entrenamiento"}</strong>
                  <span>{fecha(log.empezadoEn)}</span>
                </div>
                <div className="rp-hist-cifras">
                  <span>{duracion(log.duracionSeg)}</span>
                  <span>{log.seriesCompletadas} series</span>
                  <span>{(log.volumenKg || 0).toLocaleString("es-ES")} kg</span>
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}

export default RutinaPanel;
